import { api } from "@/lib/api";

const MB = 1024 * 1024;
const PHOTO_EXT = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
const VIDEO_EXT = ["mp4", "mov", "webm"];
export const MAX_FILES = 10;
export const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm,.mov";

const extOf = (name) => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

export const fileKind = (file) => {
  const ext = extOf(file.name);
  if (PHOTO_EXT.includes(ext) && file.size > 0 && file.size <= 15 * MB) return "photo";
  if (VIDEO_EXT.includes(ext) && file.size > 0 && file.size <= 300 * MB) return "video";
  return null;
};

export const rejectMsg = (name) =>
  `${name}: please upload a photo (JPG, PNG, WEBP, HEIC up to 15 MB) or video (MP4, MOV, WEBM up to 300 MB)`;

export const isLogoOk = (file) => PHOTO_EXT.includes(extOf(file.name)) && file.size > 0 && file.size <= 10 * MB;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function withRetry(fn, tries = 4) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      const st = e.response?.status;
      const retryable = !st || st >= 500 || st === 408 || st === 429;
      if (attempt >= tries - 1 || !retryable) throw e;
      await sleep(1000 * 2 ** attempt);
    }
  }
}

export const initUpload = async (file, projectId) =>
  (await api.post("/admin/uploads/init", { project_id: projectId, filename: file.name, size: file.size })).data;

export async function uploadFile({ file, projectId, session, onProgress, onSession }) {
  let s = session;
  if (s?.fresh === false || (s && s.received.length)) {
    try {
      s = (await api.get(`/admin/uploads/${s.upload_id}`)).data;
    } catch (e) {
      if (e.response?.status !== 404) throw e;
      s = null;
    }
  }
  if (!s) s = await initUpload(file, projectId);
  onSession({ ...s, fresh: false });
  const have = new Set(s.received);
  const lenOf = (i) => Math.min(s.chunk_size, file.size - i * s.chunk_size);
  let done = [...have].reduce((acc, i) => acc + lenOf(i), 0);
  onProgress(done / file.size);
  for (let i = 0; i < s.total_chunks; i++) {
    if (have.has(i)) continue;
    const blob = file.slice(i * s.chunk_size, i * s.chunk_size + lenOf(i));
    await withRetry(() =>
      api.put(`/admin/uploads/${s.upload_id}/chunks/${i}`, blob, {
        headers: { "Content-Type": "application/octet-stream" },
        timeout: 180000,
        onUploadProgress: (ev) => onProgress(Math.min(0.99, (done + (ev.loaded || 0)) / file.size)),
      })
    );
    done += blob.size;
    onProgress(Math.min(0.99, done / file.size));
  }
  const media = (await withRetry(() => api.post(`/admin/uploads/${s.upload_id}/complete`, null, { timeout: 180000 }), 2)).data;
  onProgress(1);
  return media;
}
